/** Number and date formatting, Russian locale throughout — the panel has one audience. */

const LOCALE = 'ru-RU';

export function formatMoney(amount: number, currency: string): string {
  try {
    return new Intl.NumberFormat(LOCALE, {
      style: 'currency',
      currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(amount);
  } catch {
    // An unknown ISO code throws; the raw pair is still readable.
    return `${formatNumber(amount, 2)} ${currency}`;
  }
}

export function formatNumber(value: number, maxDecimals = 0): string {
  return new Intl.NumberFormat(LOCALE, { maximumFractionDigits: maxDecimals }).format(value);
}

export function formatDate(iso: string): string {
  return new Intl.DateTimeFormat(LOCALE, { day: 'numeric', month: 'short', year: 'numeric' }).format(new Date(iso));
}

export function formatDateTime(iso: string): string {
  return new Intl.DateTimeFormat(LOCALE, {
    day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  }).format(new Date(iso));
}

/** "5 минут назад", "вчера" — for a sync timestamp, where the distance matters more than the clock. */
export function formatRelative(iso: string, now: Date = new Date()): string {
  const diff = (new Date(iso).getTime() - now.getTime()) / 1000;
  const abs = Math.abs(diff);
  const rtf = new Intl.RelativeTimeFormat(LOCALE, { numeric: 'auto' });
  if (abs < 60) return rtf.format(Math.round(diff), 'second');
  if (abs < 3600) return rtf.format(Math.round(diff / 60), 'minute');
  if (abs < 86_400) return rtf.format(Math.round(diff / 3600), 'hour');
  if (abs < 30 * 86_400) return rtf.format(Math.round(diff / 86_400), 'day');
  return formatDate(iso);
}

/** Russian plural: `plural(3, ['отель', 'отеля', 'отелей'])` → "3 отеля". */
export function plural(n: number, forms: [string, string, string], withNumber = true): string {
  const abs = Math.abs(n) % 100;
  const last = abs % 10;
  const form = abs > 10 && abs < 20 ? forms[2] : last === 1 ? forms[0] : last >= 2 && last <= 4 ? forms[1] : forms[2];
  return withNumber ? `${formatNumber(n)} ${form}` : form;
}

/** The first eight characters of a UUID — enough to recognise, short enough for a cell. */
export function shortId(id: string): string {
  return id.slice(0, 8);
}

/** The month name for a `YYYY-MM` period, in UTC so the label never slips a month. */
export function formatPeriod(period: string): string {
  const [year, month] = period.split('-').map(Number);
  const label = new Intl.DateTimeFormat(LOCALE, { month: 'long', year: 'numeric', timeZone: 'UTC' })
    .format(new Date(Date.UTC(year ?? 1970, (month ?? 1) - 1, 1)));
  return label.charAt(0).toUpperCase() + label.slice(1);
}

/** "12 %" — a share, one decimal at most, never "NaN %". */
export function formatPercent(part: number, whole: number, decimals = 0): string {
  if (!whole) return '—';
  return `${new Intl.NumberFormat(LOCALE, { maximumFractionDigits: decimals }).format((part / whole) * 100)} %`;
}

/** "1,2 тыс." for a tile that has no room for "1 234". */
export function formatCompact(value: number): string {
  return new Intl.NumberFormat(LOCALE, { notation: 'compact', maximumFractionDigits: 1 }).format(value);
}

/** A person's initials for an avatar: "Dilnoza Karimova" → "DK", "front@x" → "F". */
export function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return (parts[0] ?? '?').slice(0, 1).toUpperCase();
}
